import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Download, Eye, Search, Loader2, X, Plus, Package } from 'lucide-react'
import { apiGet, formatMXN } from '../lib/api'
import { paymentMethodLabel } from '../lib/payment-method-label'

interface PedidosData {
  total: number
  page: number
  pageSize: number
  pages: number
  data: Array<{
    id: string
    nrodePedido: string
    cliente: string
    email: string
    total: number
    fp: string
    paymentStatus: string
    estado: string
    fecha: string
    ciudad: string
    provincia: string
    pais: string
    carrier: string | null
    trackingCode: string | null
  }>
}

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todos los estados' },
  { value: 'draft', label: 'Borrador' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'paid', label: 'Pagado' },
  { value: 'preparing', label: 'Preparando' },
  { value: 'shipped', label: 'Enviado' },
  { value: 'delivered', label: 'Entregado' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'refunded', label: 'Reembolsado' },
]

const METHOD_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todos los métodos' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'mercadopago', label: 'Mercado Pago' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'tarjeta', label: 'Tarjeta' },
]

function statusBadge(s: string) {
  const low = (s || '').toLowerCase()
  if (low === 'delivered') return <Badge variant="success">Entregado</Badge>
  if (low === 'shipped') return <Badge variant="info">Enviado</Badge>
  if (low === 'paid') return <Badge variant="success">Pagado</Badge>
  if (low === 'preparing') return <Badge variant="warning">Preparando</Badge>
  if (low === 'pending') return <Badge variant="warning">Pendiente</Badge>
  if (low === 'cancelled') return <Badge variant="danger">Cancelado</Badge>
  if (low === 'refunded') return <Badge variant="danger">Reembolsado</Badge>
  if (low === 'draft') return <Badge>Borrador</Badge>
  return <Badge>{s || 'Sin estado'}</Badge>
}

/** Badge ÚNICO del pedido: combina pago + avance en una sola etiqueta clara. */
function orderBadge(status: string, payment: string) {
  const s = (status || '').toLowerCase()
  const p = (payment || '').toLowerCase()
  if (s === 'cancelled') return <Badge variant="danger">Cancelado</Badge>
  if (s === 'refunded' || p === 'refunded') return <Badge variant="danger">Reembolsado</Badge>
  if (s === 'delivered') return <Badge variant="success">Entregado</Badge>
  if (s === 'shipped') return <Badge variant="info">Enviado</Badge>
  if (s === 'preparing') return <Badge variant="warning">Preparando</Badge>
  if (p === 'paid' || s === 'paid') return <Badge variant="success">Pedido exitoso</Badge>
  if (p === 'failed') return <Badge variant="danger">Pago fallido</Badge>
  return <Badge variant="warning">Pendiente de pago</Badge>
}

function carrierLabel(carrier: string | null): string {
  const c = (carrier || '').toLowerCase()
  if (c === 'fedex') return 'FedEx'
  if (c === 'estafeta') return 'Estafeta'
  return carrier ? carrier.charAt(0).toUpperCase() + carrier.slice(1) : ''
}

/** Celda de guía/envío: badge con transportista + tracking, o "—" si aún no hay guía. */
function guiaCell(carrier: string | null, trackingCode: string | null) {
  if (!trackingCode) return <span className="text-ink-300">—</span>
  const label = carrierLabel(carrier)
  const full = `${label ? label + ' · ' : ''}${trackingCode}`
  return (
    <Badge variant="info">
      <span className="inline-flex items-center gap-1" title={full}>
        <Package className="size-3" />
        {label && <span>{label}</span>}
        <span className="max-w-[7rem] truncate font-mono text-[10px]">{trackingCode}</span>
      </span>
    </Badge>
  )
}

function readInitialFilters() {
  if (typeof window === 'undefined') {
    return { page: 1, q: '', status: '', method: '', from: '', to: '' }
  }
  const sp = new URLSearchParams(window.location.search)
  return {
    page: Math.max(1, parseInt(sp.get('page') || '1') || 1),
    q: sp.get('q') || '',
    status: sp.get('status') || '',
    method: sp.get('method') || '',
    from: sp.get('from') || '',
    to: sp.get('to') || '',
  }
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}

function daysAgoISO(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

export function Pedidos() {
  const navigate = useNavigate()
  const initial = useMemo(readInitialFilters, [])
  const [page, setPage] = useState(initial.page)
  const [qInput, setQInput] = useState(initial.q)
  const [q, setQ] = useState(initial.q)
  const [status, setStatus] = useState(initial.status)
  const [method, setMethod] = useState(initial.method)
  // Rango de fechas para export — por defecto últimos 30 días, no afecta listado.
  const [exportFrom, setExportFrom] = useState(initial.from || daysAgoISO(30))
  const [exportTo, setExportTo] = useState(initial.to || todayISO())
  const [showExport, setShowExport] = useState(false)

  // Sync URL params para que la pantalla sea linkeable / recargable
  useEffect(() => {
    if (typeof window === 'undefined') return
    const sp = new URLSearchParams()
    if (page > 1) sp.set('page', String(page))
    if (q) sp.set('q', q)
    if (status) sp.set('status', status)
    if (method) sp.set('method', method)
    const qs = sp.toString()
    const newUrl = `${window.location.pathname}${qs ? '?' + qs : ''}`
    window.history.replaceState(null, '', newUrl)
  }, [page, q, status, method])

  const queryString = useMemo(() => {
    const sp = new URLSearchParams()
    sp.set('page', String(page))
    if (q) sp.set('q', q)
    if (status) sp.set('status', status)
    if (method) sp.set('method', method)
    return sp.toString()
  }, [page, q, status, method])

  const { data, isLoading, isFetching } = useQuery<PedidosData>({
    queryKey: ['pedidos', page, q, status, method],
    queryFn: () => apiGet(`/api/admin/pedidos?${queryString}`),
    placeholderData: (prev) => prev,
  })

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setQ(qInput.trim())
  }

  function clearFilters() {
    setQInput('')
    setQ('')
    setStatus('')
    setMethod('')
    setPage(1)
  }

  function goToDetalle(id: string) {
    navigate({ to: '/pedidos/$id', params: { id } })
  }

  function onExport() {
    const sp = new URLSearchParams()
    if (exportFrom) sp.set('from', exportFrom)
    if (exportTo) sp.set('to', exportTo)
    if (status) sp.set('status', status)
    sp.set('format', 'xlsx')
    // Disparar descarga vía navegación directa — el browser respeta el
    // Content-Disposition: attachment del endpoint.
    window.location.href = `/api/admin/pedidos/export?${sp.toString()}`
  }

  const hasActiveFilters = q || status || method

  return (
    <>
      <Topbar
        title="Pedidos"
        subtitle={data ? `${data.total.toLocaleString('es-MX')} pedidos en total` : 'Cargando…'}
        actions={
          <>
            <button
              onClick={() => setShowExport((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <Download className="size-3.5" /> Exportar Excel
            </button>
            <button
              onClick={() => navigate({ to: '/pedidos/$id', params: { id: 'nuevo' } })}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Plus className="size-3.5" /> Nuevo pedido
            </button>
          </>
        }
      />
      <div className="p-6">
        {showExport && (
          <div className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl border border-ink-200 bg-ink-50 p-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">Desde</label>
              <input
                type="date"
                value={exportFrom}
                onChange={(e) => setExportFrom(e.target.value)}
                className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-brand-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">Hasta</label>
              <input
                type="date"
                value={exportTo}
                onChange={(e) => setExportTo(e.target.value)}
                className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-brand-500"
              />
            </div>
            <div className="text-xs text-ink-500">
              {status ? (
                <span>
                  Solo estado: <strong className="text-ink-800">{status}</strong>
                </span>
              ) : (
                <span>Todos los estados (cambia el filtro de arriba para acotar)</span>
              )}
            </div>
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={() => setShowExport(false)}
                className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-100"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={onExport}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
              >
                <Download className="size-3.5" /> Descargar XLSX
              </button>
            </div>
          </div>
        )}

        <form onSubmit={onSearch} className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-64">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Buscar por nro de pedido, cliente o email…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={method}
            onChange={(e) => {
              setMethod(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
          >
            {METHOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Buscar
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <X className="size-3.5" /> Limpiar
            </button>
          )}
        </form>

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando pedidos…</p>
            </div>
          )}

          {data && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="w-10 px-4 py-3"><input type="checkbox" /></th>
                      <th className="px-4 py-3 font-medium">Pedido</th>
                      <th className="px-4 py-3 font-medium">Cliente</th>
                      <th className="px-4 py-3 font-medium">Fecha</th>
                      <th className="px-4 py-3 font-medium">Envío a</th>
                      <th className="px-4 py-3 font-medium text-right">Total</th>
                      <th className="px-4 py-3 font-medium">Pago</th>
                      <th className="px-4 py-3 font-medium">Estado</th>
                      <th className="px-4 py-3 font-medium">Envío/Guía</th>
                      <th className="w-12 px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.length === 0 && (
                      <tr>
                        <td colSpan={10} className="px-4 py-12 text-center text-sm text-ink-500">
                          No se encontraron pedidos con esos filtros.
                        </td>
                      </tr>
                    )}
                    {data.data.map((p) => (
                      <tr
                        key={p.id}
                        onClick={() => goToDetalle(p.id)}
                        className="cursor-pointer border-t border-ink-100 transition-colors hover:bg-ink-50"
                      >
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" />
                        </td>
                        <td className="px-4 py-3 font-mono text-sm font-medium text-ink-900">
                          #{p.nrodePedido}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-ink-900">{p.cliente}</div>
                          <div className="truncate text-xs text-ink-500">{p.email}</div>
                        </td>
                        <td className="px-4 py-3 text-ink-700">{p.fecha}</td>
                        <td className="px-4 py-3 text-ink-700">
                          <div>{p.provincia}</div>
                          <div className="text-[10px] text-ink-500">{p.pais}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-ink-900">
                          {formatMXN(p.total)}
                        </td>
                        <td className="px-4 py-3 text-xs text-ink-700">{paymentMethodLabel(p.fp)}</td>
                        <td className="px-4 py-3">{orderBadge(p.estado, p.paymentStatus)}</td>
                        <td className="px-4 py-3">{guiaCell(p.carrier, p.trackingCode)}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => goToDetalle(p.id)}
                            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                            title="Ver detalle"
                          >
                            <Eye className="size-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                <div className="text-ink-500">
                  Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                  <strong className="text-ink-900">{data.pages.toLocaleString('es-MX')}</strong>{' '}
                  ({data.total.toLocaleString('es-MX')} pedidos)
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button className="rounded-md bg-brand-600 px-3 py-1 text-white">
                    {page}
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= data.pages}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
